import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { UserAvatar } from '../../../common/Avatar'
import { theme } from '../../../../theme/theme'
import { IconChoices } from '../../../common/Icon'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useModal } from '../../../../hooks/useModal'
import { ToastType } from '../../../common/Toast/toast-type'
import { Dropdown } from '../../../common/Dropdown'
import { CreateUpdateAdminModal } from '../CreateUpdateAdminModal'
import { Button, ButtonVariant } from '../../../common/Button'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { patchSociety, removeAdminUser } from '../../../../api/societies'

interface AdminRightsListItemProps {
  society: ISocietiesModel
  admin: {
    user: IUserModel & { fullName: string }
    role: string
  }
}

export const AdminRightsListItem = ({
  admin,
  society,
}: AdminRightsListItemProps): React.ReactNode => {
  const { userId } = useAuth()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { show: showEditModal, close: closeEditModal } = useModal()
  const {
    show: showDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
  } = useModal()
  const currentUserId = userId

  const adminUser = admin.user

  const { mutate: mutateUpdateMasterAdmin } = useMutation({
    mutationFn: (params: { societyId: string; adminUserId: string }) => {
      return patchSociety({
        id: params.societyId,
        body: { masterAdminUserId: params.adminUserId },
      })
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('adminRightsView.flashMessage.masterAdminChangedSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('adminRightsView.flashMessage.masterAdminChangedFailure')
      )
    },
  })
  const dropdownMakeAdminOption = society.masterAdminUserId ===
    currentUserId && {
    value: 'makeMasterAdmin',
    label: translate('adminRightsView.makeMasterAdminTitle'),
    onClick: async () => {
      mutateUpdateMasterAdmin({
        adminUserId: adminUser._id,
        societyId: society._id,
      })
    },
  }

  const { mutate: mutateRemoveAdminUser } = useMutation({
    mutationFn: removeAdminUser,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('adminRightsView.flashMessage.adminRemoveSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('adminRightsView.flashMessage.adminRemoveFailure')
      )
    },
  })
  const deleteUser = async (): Promise<void> => {
    mutateRemoveAdminUser({
      userId: adminUser._id,
      id: society._id,
    })
  }

  const dropdownDeleteOption = {
    value: 'remove',
    label: translate('common.actions.remove'),
    onClick: openDeleteConfirmationModal,
    destructive: true,
  }

  const dropdownOptions = dropdownMakeAdminOption
    ? [dropdownMakeAdminOption, dropdownDeleteOption]
    : [dropdownDeleteOption]

  return (
    <div className="flex flex-row items-center border-b pb-4">
      <div className="flex flex-10 flex-row items-center">
        <UserAvatar user={adminUser} />
        <div className="ml-4 flex flex-col justify-start">
          <p style={theme.textVariants.baseBold}>{admin.role}</p>
          <p
            className="text-neutral-30"
            style={theme.textVariants.base}
            translate="no"
          >
            {adminUser.fullName}
          </p>
        </div>
      </div>
      {society.masterAdminUserId !== adminUser._id && (
        <Dropdown options={dropdownOptions} className="mr-3">
          <Button
            variant={ButtonVariant.TEXT}
            className="h-10 w-10 border-none bg-transparent hover:bg-neutral-90"
            icon={IconChoices.MORE}
            disableTabIndex
          />
        </Dropdown>
      )}
      <CreateUpdateAdminModal show={showEditModal} close={closeEditModal} />
      <ConfirmationModal
        title={translate('adminRightsView.alert.deleteAdminTitle')}
        description={translate('adminRightsView.alert.deleteAdminText')}
        show={showDeleteConfirmationModal}
        close={closeDeleteConfirmationModal}
        onConfirm={deleteUser}
      />
    </div>
  )
}
